import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, getApiErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { currency } from "@/lib/format";
import { Plus, Trash2, ArrowLeft, Download } from "lucide-react";
import { toast } from "sonner";

export default function QuotationNew({ role }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const leadId = params.get("lead_id");
  const patientId = params.get("patient_id");

  const [hospitals, setHospitals] = useState([]);
  const [form, setForm] = useState({
    patient_name: "", treatment: "", hospital_id: "", currency: "USD",
    notes: "", valid_until: "", tax_rate: 5, discount: 0,
    line_items: [{ description: "", quantity: 1, unit_price: 0 }],
    lead_id: leadId || null, patient_id: patientId || null,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/hospitals"); setHospitals(data);
      if (leadId) {
        try { const r = await api.get(`/leads/${leadId}`); setForm((p) => ({ ...p, patient_name: r.data.patient_name, treatment: r.data.treatment, hospital_id: r.data.preferred_hospital_id || "" })); } catch {/* ignore */}
      } else if (patientId) {
        try { const r = await api.get(`/patients/${patientId}`); setForm((p) => ({ ...p, patient_name: r.data.name, treatment: r.data.treatment, hospital_id: r.data.hospital_id || "" })); } catch {/* ignore */}
      }
    })();
  }, [leadId, patientId]);

  const totals = useMemo(() => {
    const items = form.line_items.map((li) => ({ ...li, amount: (+li.quantity || 0) * (+li.unit_price || 0) }));
    const subtotal = items.reduce((s, x) => s + x.amount, 0);
    const tax = subtotal * ((+form.tax_rate || 0) / 100);
    const total = subtotal + tax - (+form.discount || 0);
    return { items, subtotal, tax, total };
  }, [form.line_items, form.tax_rate, form.discount]);

  const updateLine = (i, field, val) => {
    const lines = [...form.line_items];
    lines[i] = { ...lines[i], [field]: val };
    setForm({ ...form, line_items: lines });
  };
  const addLine = () => setForm({ ...form, line_items: [...form.line_items, { description: "", quantity: 1, unit_price: 0 }] });
  const removeLine = (i) => setForm({ ...form, line_items: form.line_items.filter((_, idx) => idx !== i) });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        line_items: form.line_items.map((li) => ({
          description: li.description, quantity: +li.quantity || 0, unit_price: +li.unit_price || 0, amount: (+li.quantity || 0) * (+li.unit_price || 0),
        })),
        tax_rate: +form.tax_rate || 0, discount: +form.discount || 0,
      };
      if (!payload.hospital_id) delete payload.hospital_id;
      if (!payload.valid_until) delete payload.valid_until;
      if (!payload.lead_id) delete payload.lead_id;
      if (!payload.patient_id) delete payload.patient_id;
      const { data } = await api.post("/quotations", payload);
      toast.success(`Quotation ${data.quote_number} created`);
      navigate(`/${role}/quotations`);
    } catch (err) { toast.error(getApiErrorMessage(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="new-quotation-page">
      <Link to={`/${role}/quotations`} className="inline-flex items-center gap-1.5 text-xs text-[#5C6660] hover:text-[#2A4D3E] mb-4">
        <ArrowLeft size={14} /> Back to quotations
      </Link>
      <PageHeader eyebrow="Estimates" title="New quotation"
        subtitle="Build a detailed treatment estimate with line items, taxes and discounts." />

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6">
            <div className="font-heading font-semibold text-lg mb-4 text-[#2A332C]">Patient & treatment</div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Patient name *" value={form.patient_name} onChange={(v) => setForm({ ...form, patient_name: v })} required testid="q-patient" />
              <Field label="Treatment *" value={form.treatment} onChange={(v) => setForm({ ...form, treatment: v })} required testid="q-treatment" />
              <SelectField label="Hospital" value={form.hospital_id} onChange={(v) => setForm({ ...form, hospital_id: v })} testid="q-hospital">
                <option value="">— None —</option>
                {hospitals.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
              </SelectField>
              <SelectField label="Currency" value={form.currency} onChange={(v) => setForm({ ...form, currency: v })} testid="q-currency">
                {["USD", "EUR", "GBP", "AED", "INR"].map((c) => <option key={c}>{c}</option>)}
              </SelectField>
              <Field label="Valid until" type="date" value={form.valid_until} onChange={(v) => setForm({ ...form, valid_until: v })} testid="q-valid-until" />
            </div>
          </div>

          <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6" data-testid="line-items-section">
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading font-semibold text-lg text-[#2A332C]">Line items</div>
              <button type="button" onClick={addLine} data-testid="add-line-item"
                className="text-xs bg-[#E6F0EB] text-[#2A4D3E] font-semibold px-3 py-1.5 rounded-full hover:bg-[#D8E8DF] flex items-center gap-1">
                <Plus size={12} /> Add line
              </button>
            </div>
            <div className="space-y-2">
              {form.line_items.map((li, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`line-${i}`}>
                  <input className="col-span-6 border border-[#E8E6E1] rounded-xl px-3 py-2 bg-[#FDFCFB] text-sm focus:outline-none focus:border-[#2A4D3E]"
                    placeholder="Description" value={li.description} onChange={(e) => updateLine(i, "description", e.target.value)} data-testid={`line-desc-${i}`} />
                  <input type="number" className="col-span-2 border border-[#E8E6E1] rounded-xl px-3 py-2 bg-[#FDFCFB] text-sm focus:outline-none focus:border-[#2A4D3E]"
                    placeholder="Qty" value={li.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} data-testid={`line-qty-${i}`} />
                  <input type="number" className="col-span-2 border border-[#E8E6E1] rounded-xl px-3 py-2 bg-[#FDFCFB] text-sm focus:outline-none focus:border-[#2A4D3E]"
                    placeholder="Unit price" value={li.unit_price} onChange={(e) => updateLine(i, "unit_price", e.target.value)} data-testid={`line-price-${i}`} />
                  <div className="col-span-1 text-right text-sm font-medium text-[#2A332C]">
                    {currency((+li.quantity || 0) * (+li.unit_price || 0), form.currency)}
                  </div>
                  <button type="button" onClick={() => removeLine(i)} className="col-span-1 text-[#C0392B] hover:bg-[#FDEDEC] p-2 rounded-lg" data-testid={`line-delete-${i}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6">
            <div className="font-heading font-semibold text-lg mb-3 text-[#2A332C]">Notes</div>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows="3"
              data-testid="q-notes"
              className="w-full border border-[#E8E6E1] rounded-xl px-4 py-2.5 bg-[#FDFCFB] text-sm focus:outline-none focus:border-[#2A4D3E]" />
          </div>
        </div>

        <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6 h-fit sticky top-6" data-testid="quotation-summary">
          <div className="font-heading font-semibold text-lg mb-4 text-[#2A332C]">Summary</div>
          <div className="space-y-3 text-sm">
            <Row label="Subtotal" value={currency(totals.subtotal, form.currency)} />
            <div className="flex items-center justify-between gap-2">
              <label className="text-[#5C6660]">Tax %</label>
              <input type="number" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
                data-testid="q-tax-rate"
                className="w-20 border border-[#E8E6E1] rounded-lg px-2 py-1 text-right text-sm focus:outline-none focus:border-[#2A4D3E]" />
            </div>
            <Row label="Tax amount" value={currency(totals.tax, form.currency)} />
            <div className="flex items-center justify-between gap-2">
              <label className="text-[#5C6660]">Discount</label>
              <input type="number" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })}
                data-testid="q-discount"
                className="w-24 border border-[#E8E6E1] rounded-lg px-2 py-1 text-right text-sm focus:outline-none focus:border-[#2A4D3E]" />
            </div>
            <div className="pt-3 mt-3 border-t border-[#E8E6E1] flex items-center justify-between">
              <span className="font-heading font-semibold text-[#2A4D3E]">Total</span>
              <span className="font-heading text-2xl font-semibold text-[#2A4D3E]" data-testid="q-total">{currency(totals.total, form.currency)}</span>
            </div>
          </div>
          <button type="submit" disabled={submitting} data-testid="submit-quotation"
            className="mt-5 w-full bg-[#2A4D3E] text-white rounded-full px-5 py-3 text-sm font-medium hover:bg-[#1E3B2F] disabled:opacity-50">
            {submitting ? "Creating…" : "Create quotation"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid, ...rest }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#5C6660] mb-1.5">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} {...rest}
        className="w-full border border-[#E8E6E1] rounded-xl px-4 py-2.5 bg-[#FDFCFB] text-[#2A332C] focus:outline-none focus:border-[#2A4D3E] focus:ring-1 focus:ring-[#2A4D3E]" />
    </div>
  );
}
function SelectField({ label, value, onChange, testid, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#5C6660] mb-1.5">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full border border-[#E8E6E1] rounded-xl px-4 py-2.5 bg-[#FDFCFB] text-[#2A332C] focus:outline-none focus:border-[#2A4D3E] focus:ring-1 focus:ring-[#2A4D3E]">
        {children}
      </select>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#5C6660]">{label}</span>
      <span className="font-medium text-[#2A332C]">{value}</span>
    </div>
  );
}
